import { VIRTUAL_SCROLL_STRATEGY } from '@angular/cdk/scrolling';
import {
  AfterViewInit,
  Directive,
  Input,
  OnDestroy,
  OnInit,
  forwardRef,
  inject,
} from '@angular/core';
import { Store } from '@ngxs/store';
import { GroupedUnassignedUserDataV2 } from '@wilson/interfaces';
import {
  CustomVirtualScrollStrategy,
  GanttOverlapHelperService,
  calculateHeightsForShiftWithActivities,
} from '@wilson/shift-timeline/assignments-gantt-chart';
import { TimelineOverlapService } from '@wilson/shift-timeline/services';
import { ShiftTimelineUnassignedRegionState } from '@wilson/shift-timeline/state';
import {
  BehaviorSubject,
  Subscription,
  combineLatest,
  debounceTime,
} from 'rxjs';

@Directive({
  selector:
    'cdk-virtual-scroll-viewport [wilsonUnassignedServicesAndJobsVirtualScrollStrategyDirective]',
  standalone: true,
  providers: [
    {
      provide: VIRTUAL_SCROLL_STRATEGY,
      useFactory: (
        dir: UnassignedServicesAndJobsVirtualScrollStrategyDirectiveDirective,
      ) => {
        return dir.scrollStrategy;
      },
      deps: [
        forwardRef(
          () =>
            UnassignedServicesAndJobsVirtualScrollStrategyDirectiveDirective,
        ),
      ],
    },
  ],
})
export class UnassignedServicesAndJobsVirtualScrollStrategyDirectiveDirective
  implements AfterViewInit, OnDestroy, OnInit
{
  private timelineOverlapService = inject(TimelineOverlapService);
  private ganttOverlapHelperService = inject(GanttOverlapHelperService);
  private store = inject(Store);

  scrollStrategy: CustomVirtualScrollStrategy = new CustomVirtualScrollStrategy(
    [],
  );

  private unassignedDataSubject = new BehaviorSubject<
    GroupedUnassignedUserDataV2[] | null
  >([]);
  private subscriptions = new Subscription();

  private adjustSectionSubject = new BehaviorSubject<Date>(new Date());

  @Input({ required: true })
  set userTimelinesInfo(data: GroupedUnassignedUserDataV2[] | null) {
    this.unassignedDataSubject.next(data);
  }

  @Input({
    required: true,
  })
  defaultRowHeightPx!: number;

  ngOnInit(): void {
    this.subscriptions.add(
      this.store
        .select(ShiftTimelineUnassignedRegionState.isUnassignedRegionExpanded)
        .subscribe((isExpanded) => {
          this.adjustSectionSubject.next(new Date());
          if (isExpanded) {
            this.scrollStrategy.updateBufferAfter(6);
          } else {
            this.scrollStrategy.updateBufferAfter(4);
          }
        }),
    );
  }

  ngAfterViewInit(): void {
    this.subscriptions.add(
      combineLatest([this.unassignedDataSubject, this.adjustSectionSubject])
        .pipe(debounceTime(300))
        .subscribe(([data]) => {
          if (data) {
            let heights = [];
            const overlapData = data.map((row) => {
              const combinedServicesAndJobs = [...row.services, ...row.jobs];
              const userDataWithOverlapLevels =
                this.timelineOverlapService.determineOverlaps(
                  combinedServicesAndJobs,
                );

              const itemsWithLevelsAndHeights =
                this.ganttOverlapHelperService.calculateOverlapLevelsForActivities(
                  userDataWithOverlapLevels,
                  this.defaultRowHeightPx,
                );

              return itemsWithLevelsAndHeights;
            });

            heights = calculateHeightsForShiftWithActivities(
              overlapData,
              this.defaultRowHeightPx,
            );
            this.scrollStrategy.updateItemHeights(heights);
          } else {
            this.scrollStrategy.updateItemHeights([]);
          }
        }),
    );
  }

  ngOnDestroy(): void {
    this.subscriptions?.unsubscribe();
  }
}
